/* ==========================================================================
   Buffalo Barbecue Quest, journal behaviour
   Vanilla JS. GSAP + SplitText are optional and only used for the masthead.
   Port note: in Webflow this goes in Page Settings > Before </body>, never an
   HTML Embed. In Shopify it becomes assets/bbqq-journal.js.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('js-ready');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     1. Masthead reveal (archive). The one authored moment on the page.
        Masked lines, characters rise from below, expo.out, runs once.
     ------------------------------------------------------------------ */
  function masthead() {
    var el = document.querySelector('.masthead-title');
    var clear = function () { root.classList.remove('reveal-pending'); };
    if (!el) { clear(); return; }
    if (reduceMotion || !window.gsap || !window.SplitText) { clear(); return; }

    window.gsap.registerPlugin(window.SplitText);

    var ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    ready.then(function () {
      window.SplitText.create(el, {
        type: 'lines,words,chars',
        mask: 'lines',
        autoSplit: true,
        linesClass: 'masthead-line',
        onSplit: function (self) {
          clear();
          return window.gsap.from(self.chars, {
            yPercent: 110,
            duration: 0.9,
            ease: 'expo.out',
            stagger: { each: 0.018, from: 'start' }
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     2. Scroll reveal for journal entries. Fires once per element.
     ------------------------------------------------------------------ */
  function reveals() {
    var items = document.querySelectorAll('.reveal');
    if (!items.length) return;

    // Stagger within a parent, capped so late items never wait long
    var parents = new Map();
    items.forEach(function (el) {
      var p = el.parentElement;
      var n = parents.get(p) || 0;
      el.style.setProperty('--i', String(n % 6));
      parents.set(p, n + 1);
    });

    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.setAttribute('data-visible', ''); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-visible', '');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------
     3. Reading progress (single). scaleX only, rAF throttled.
     ------------------------------------------------------------------ */
  function progress() {
    var bar = document.querySelector('.progress-bar');
    var article = document.querySelector('.article');
    if (!bar || !article) return;
    var ticking = false;
    function update() {
      var rect = article.getBoundingClientRect();
      var top = rect.top + window.scrollY;
      var vh = window.innerHeight;
      var start = top - vh * 0.6;
      var end = top + rect.height - vh * 0.6;
      var p = (window.scrollY - start) / (end - start);
      p = Math.max(0, Math.min(1, p));
      bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------
     4. Table of contents (single). Built from h2s, active state tracked.
     ------------------------------------------------------------------ */
  function slugify(text) {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }
  function toc() {
    var list = document.querySelector('.toc-list');
    var article = document.querySelector('.article');
    if (!list || !article) return;
    var heads = article.querySelectorAll('h2');
    if (!heads.length) { var nav = list.closest('.toc'); if (nav) nav.hidden = true; return; }

    var links = [];
    heads.forEach(function (h) {
      if (!h.id) h.id = slugify(h.textContent);
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      list.appendChild(li);
      links.push(a);
    });

    // Active state: the last heading whose top has passed 40% of the viewport.
    // Scroll-driven rather than an observer so jumps (TOC clicks, deep links)
    // land on the right item too.
    var ticking = false;
    function setActive() {
      var line = window.innerHeight * 0.4;
      var activeIndex = -1;
      heads.forEach(function (h, i) {
        if (h.getBoundingClientRect().top <= line) activeIndex = i;
      });
      links.forEach(function (a, i) { a.classList.toggle('is-active', i === activeIndex); });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(setActive); ticking = true; }
    }, { passive: true });
    window.addEventListener('resize', setActive);
    setActive();
  }

  /* ------------------------------------------------------------------
     5. Share row (single). Copy link with an inline confirmation, no alert.
     ------------------------------------------------------------------ */
  function share() {
    var url = window.location.href;
    var title = document.title;
    var msg = document.querySelector('.share-msg');

    document.querySelectorAll('[data-share]').forEach(function (btn) {
      var type = btn.getAttribute('data-share');
      if (type === 'facebook') {
        btn.href = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(url);
      } else if (type === 'x') {
        btn.href = 'https://twitter.com/intent/tweet?url=' + encodeURIComponent(url) + '&text=' + encodeURIComponent(title);
      } else if (type === 'copy') {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          var done = function () {
            btn.classList.add('is-copied');
            if (msg) msg.textContent = 'Link copied';
            window.setTimeout(function () {
              btn.classList.remove('is-copied');
              if (msg) msg.textContent = '';
            }, 2200);
          };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done, function () { if (msg) msg.textContent = url; });
          } else if (msg) {
            msg.textContent = url;
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     6. Product rail arrows (single). Native scroll-snap does the rest.
     ------------------------------------------------------------------ */
  function rail() {
    document.querySelectorAll('[data-rail]').forEach(function (wrap) {
      var track = wrap.querySelector('.rail');
      if (!track) return;
      wrap.querySelectorAll('[data-rail-dir]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var card = track.querySelector('.card-product');
          var gap = 16;
          var step = card ? card.getBoundingClientRect().width + gap : 280;
          var dir = btn.getAttribute('data-rail-dir') === 'next' ? 1 : -1;
          track.scrollBy({ left: step * dir, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });
    });
  }

  masthead();
  reveals();
  progress();
  toc();
  share();
  rail();
})();
